import { MouseEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { OdinIcon } from '@/components/ui/odin/odin-icon'
import { Spinner } from '@/components/ui/spinner'
import { downloadSignedUrl } from 'api/uploads'
import { showErrorToast } from 'utils/toast'

type DownloadFileButtonProps = {
  /** Stored (bare) or already signed upload url: it is (re)signed on click. */
  url: string
  /** Name the file is saved under, extension included. */
  fileName: string
  className?: string
}

/** Icon button that saves an upload (see downloadSignedUrl), with a spinner while the file is fetched. */
export const DownloadFileButton = ({ url, fileName, className }: DownloadFileButtonProps) => {
  const { t } = useTranslation()
  const [downloading, setDownloading] = useState(false)

  const onClick = async (event: MouseEvent) => {
    // may sit inside a clickable card: the card must not open the file too
    event.stopPropagation()
    setDownloading(true)
    try {
      if (!(await downloadSignedUrl(url, fileName))) showErrorToast(t('downloadFailed'))
    } finally {
      setDownloading(false)
    }
  }

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      onClick={onClick}
      disabled={downloading}
      title={t('download')}
      aria-label={`${t('download')} ${fileName}`}
      className={className}
    >
      {downloading ? <Spinner className="size-4" /> : <OdinIcon icon={['far', 'arrow-down-to-line']} className="size-4" />}
    </Button>
  )
}
